import { createStyles, Stack } from '@mantine/core';
import { useGetAiAccessStatus } from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Banner } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';
import { useAIConversation, useAuthUser } from '../../api';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { DelayedRender } from '../DelayedRender/DelayedRender';
import { DOCUMENTATION_WIDTH } from '../Documentation/constants';
import { AIChatConversation } from './AIChatConversation';
import AIChatEmpty from './AIChatEmpty';
import { AIPromptInput } from './AIPromptInput';
import { AiViewportWrapper } from './AiViewportWrapper';
import { ChatBanner } from './ChatBanner';
import { useAIAssistantContext } from './hooks';
import { MessageGroupSkeleton } from './Messages/MessageGroupSkeleton';
import { ScrollDownButton } from './ScrollDownButton';
import { AIAssistantMode } from './types';

const useStyles = createStyles(
	(theme, { isSidebar }: { isSidebar: boolean }) => ({
		wrapper: {
			width: '100%',
			height: '100%',
			paddingBottom: theme.spacing.xl,
			gap: 0,
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',

			[theme.fn.smallerThan('lg')]: {
				minWidth: theme.other.width.md,
			},
		},
		userInfoBanner: {
			justifyContent: 'center',
			paddingBottom: theme.spacing.md,
			display: isSidebar ? 'none' : 'flex',
			maxWidth: `${DOCUMENTATION_WIDTH}px`,
			width: '100%',
			margin: '0 auto',
			'& .mantine-Group-root': {
				maxWidth: `${DOCUMENTATION_WIDTH}px`,
				width: '100%',
			},
		},
	})
);

export const AIChat = observer(() => {
	const store = useAIAssistantContext();
	const scrollContainerRef = useRef<VirtuosoHandle | null>(null);
	const [isAtBottom, setIsAtBottom] = useState(true);

	const { classes } = useStyles({
		isSidebar: store.mode === AIAssistantMode.SIDEBAR,
	});

	const { user } = useAuthUser();
	const { hasPermission: canUseAiChartTool } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Dashboards.Manage',
	});
	const { data } = useGetAiAccessStatus({});

	const {
		isLoading,
		submit,
		stop,
		inputPrompt,
		setInputPrompt,
		isWaitingAI,
		messages,
		rerenderKey,
		retryPrompt,
		onAttachmentUpload,
		onAttachmentRemove,
		attachments,
		activeAiPersonaId,
	} = useAIConversation({ id: store.id });

	const currentUserIsOwner = useMemo(
		() => isNil(messages[0]?.user?.id) || user?.id === messages[0]?.user?.id,
		[messages, user?.id]
	);

	const isEmpty = useMemo(() => store.id === 'new', [store.id]);

	const showInput = useMemo(
		() =>
			data?.restriction_info.restriction_type !==
				'workspace_settings_disabled' &&
			(isEmpty || (messages && messages.length >= 1 && currentUserIsOwner)),
		[
			data?.restriction_info.restriction_type,
			isEmpty,
			messages,
			currentUserIsOwner,
		]
	);

	const showQuestionEducationBanner = useMemo(() => {
		const hasUnansweredPrompts = messages?.some(
			(message) => message.benchmark_raw?.fail_reason
		);

		return (
			hasUnansweredPrompts &&
			currentUserIsOwner &&
			user?.role === UserRole.VIEWER
		);
	}, [messages, currentUserIsOwner, user?.role]);

	const showMessageLimitBanner = useMemo(
		() => messages.length >= 10,
		[messages]
	);

	const showCreditRestrictionBanner = useMemo(
		() => data?.restriction_info.restriction_type === 'insufficient_credits',
		[data?.restriction_info.restriction_type]
	);

	const lastUserPrompt = useMemo(() => {
		const userPrompts = messages.filter((msg) => msg.user_generated);
		return userPrompts[userPrompts.length - 1];
	}, [messages]);

	const initialChartState = useMemo(
		() => lastUserPrompt?.chart ?? false,
		[lastUserPrompt]
	);

	const initialWebSearchState = useMemo(
		() => lastUserPrompt?.web_search ?? false,
		[lastUserPrompt]
	);

	const handleScrollToBottom = useCallback(
		(align: 'start' | 'end') => {
			scrollContainerRef.current?.scrollToIndex({
				index: messages.length - 1,
				align,
				behavior: 'smooth',
			});
		},
		[messages.length]
	);

	return (
		<Stack className={classes.wrapper}>
			{!currentUserIsOwner && !isEmpty && (
				<AiViewportWrapper>
					<Banner
						tone="neutral"
						message={`You are viewing a conversation between Secoda AI and ${messages[0]?.user?.display_name ?? 'a team member'}.`}
					/>
				</AiViewportWrapper>
			)}
			{isEmpty && (
				<AiViewportWrapper h="100%">
					<AIChatEmpty activeAiPersonaId={activeAiPersonaId} />
				</AiViewportWrapper>
			)}
			{isLoading && (
				<AiViewportWrapper h="100%">
					<DelayedRender>
						<MessageGroupSkeleton />
					</DelayedRender>
				</AiViewportWrapper>
			)}
			{!isEmpty && !isLoading && (
				<AIChatConversation
					messages={messages}
					onRetry={retryPrompt}
					atBottomStateChange={setIsAtBottom}
					handle={(ref) => {
						scrollContainerRef.current = ref;
					}}
					onScrollToBottom={handleScrollToBottom}
				/>
			)}
			<AiViewportWrapper pos="relative">
				{showInput && !isEmpty && !isLoading && (
					<ScrollDownButton
						shouldShow={!isAtBottom}
						onClick={() => handleScrollToBottom('end')}
					/>
				)}
				<ChatBanner
					showQuestionEducationBanner={showQuestionEducationBanner}
					showMessageLimitBanner={showMessageLimitBanner}
					showCreditRestrictionBanner={showCreditRestrictionBanner}
					creditRestrictionReason={data?.restriction_info.reason || undefined}
				/>
				{showInput && (
					<AIPromptInput
						value={inputPrompt}
						onChange={setInputPrompt}
						onSubmit={submit}
						isLoading={isWaitingAI}
						onStop={stop}
						onAttachmentUpload={onAttachmentUpload}
						onAttachmentRemove={onAttachmentRemove}
						editorReRenderKey={rerenderKey}
						attachments={attachments}
						activeAiPersonaId={activeAiPersonaId}
						initialIsWebSearch={initialWebSearchState}
						initialIsChart={initialChartState}
						canUseAiChartTool={canUseAiChartTool}
					/>
				)}
			</AiViewportWrapper>
		</Stack>
	);
});
